import { useEffect, useRef, useState } from "react";

// Comma-separated tags input that ACTUALLY respects the space bar.
//
// The old inline pattern (`value={tags.join(", ")}` + onChange that splits →
// trims → filters empty → joins back) stripped every trailing space and
// caused React to remount the caret on every keystroke — users reported
// having to click into the field to type each individual letter, and
// spaces were silently discarded. The root cause was that `.trim()` inside
// onChange ate any in-progress trailing space before the state update, so
// on the next render the input value snapped back to a form with no space.
//
// This component holds the raw string locally and only commits the parsed
// array on blur (or when the user explicitly finishes a tag with a comma
// or presses Enter). External updates to `value` are respected only when
// the input is NOT focused, so parent re-renders can't yank the user's
// in-progress typing.
export default function TagsInput({
    value,
    onChange,
    placeholder = "",
    className = "",
    inputClassName = "",
    "data-testid": dataTestId,
    disabled = false,
}) {
    const [raw, setRaw] = useState(() => (Array.isArray(value) ? value.join(", ") : ""));
    const focused = useRef(false);

    // Sync incoming array → string ONLY while the input is not focused.
    // Otherwise the user's live typing gets clobbered by parent renders.
    useEffect(() => {
        if (focused.current) return;
        const external = Array.isArray(value) ? value.join(", ") : "";
        setRaw(external);
    }, [value]);

    const commit = () => {
        const parsed = raw
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        // Dedup while preserving order
        const seen = new Set();
        const deduped = parsed.filter((t) => {
            const k = t.toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
        onChange(deduped);
    };

    return (
        <input
            type="text"
            disabled={disabled}
            className={inputClassName || className || "w-full bg-[#0a0a0a] border border-[#3a3a3a] text-white px-3 py-2 text-sm"}
            placeholder={placeholder}
            data-testid={dataTestId}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            onFocus={() => {
                focused.current = true;
            }}
            onBlur={() => {
                focused.current = false;
                commit();
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    commit();
                }
            }}
        />
    );
}
